import {BankAccount} from "../../domain/bank-account.entity";
import {
  BankValidationResult,
  PayoutCountry,
  PayoutBank,
  BankBranch,
  ValidateBankParams,
  ConnectBankParams,
  UpdateBankParams,
} from "../../domain/bank-account.types";
import {httpClientV2} from "../../../../infrastructure/http";
import {BankAccountMapperV2} from "../mappers/bank-account.mapper.v2";
import {
  V2ConnectedBankResponse,
  V2PayoutCountriesResponse,
  V2PayoutBanksResponse,
  V2BankBranchesResponse,
  V2ValidateAccountResponse,
  V2ConnectBankResponse,
  V2UpdateCompleteResponse,
} from "../mappers/bank-account.mapper.types";
import {BankAccountRepository, InitiateUpdateParams} from "./bank-account.repository";
import {isEmptyConnectedBankAccountError} from "../../utils/empty-connected-bank-error";

/**
 * v2 API implementation of BankAccountRepository.
 *
 * Key differences from v1:
 * - Endpoints under /payments/bank instead of /banks
 * - Update flow is two-step: initiate (sends OTP) then complete (with otpCode)
 * - Banks endpoint returns full institution data
 *
 * Pure TypeScript - no React imports.
 */
export class BankAccountRepositoryV2 implements BankAccountRepository {
  async getConnectedBankAccount(): Promise<BankAccount | null> {
    try {
      const response = await httpClientV2.get<V2ConnectedBankResponse>("payments/bank");
      return BankAccountMapperV2.fromConnectedBankResponse(response);
    } catch (error: unknown) {
      // Handle "no bank account" error gracefully
      // v2 may return 404 or specific error for no bank
      if (isEmptyConnectedBankAccountError(error)) {
        return null;
      }
      throw error;
    }
  }

  async validateBankAccount(params: ValidateBankParams): Promise<BankValidationResult> {
    const request = BankAccountMapperV2.toValidateBankRequest(params);
    const response = await httpClientV2.post<V2ValidateAccountResponse>(
      "payments/bank/enquiry",
      request
    );
    return BankAccountMapperV2.toValidationResult(response);
  }

  async connectBankAccount(params: ConnectBankParams): Promise<BankAccount> {
    const request = BankAccountMapperV2.toConnectBankRequest(params);
    const response = await httpClientV2.post<V2ConnectBankResponse>(
      "payments/bank/create",
      request
    );
    return BankAccountMapperV2.toDomain(response.data);
  }

  /**
   * v1-style update is not supported in v2.
   * Use initiateUpdateBankAccount + completeUpdateBankAccount instead.
   * @throws Error explaining the v2 two-step flow
   */
  async updateBankAccount(_params: UpdateBankParams): Promise<BankAccount> {
    throw new Error(
      "v2 bank update uses two-step flow. Use initiateUpdateBankAccount() to send OTP, " +
        "then completeUpdateBankAccount() with the OTP to complete."
    );
  }

  /**
   * Initiate a bank account update.
   * Sends OTP to user's registered phone/email.
   */
  async initiateUpdateBankAccount(params: InitiateUpdateParams): Promise<void> {
    const request = BankAccountMapperV2.toInitiateUpdateRequest({
      ...params,
      otp: "", // Not used in initiate request
    });
    await httpClientV2.post<{data: Record<string, never>}>(
      "payments/bank/update",
      request
    );
  }

  /**
   * Complete a bank account update.
   * Called after user enters OTP from initiateUpdateBankAccount.
   */
  async completeUpdateBankAccount(otp: string): Promise<BankAccount> {
    const request = BankAccountMapperV2.toCompleteUpdateRequest(otp);
    const response = await httpClientV2.patch<V2UpdateCompleteResponse>(
      "payments/bank/update/complete",
      request
    );
    return BankAccountMapperV2.toDomain(response.data);
  }

  async getPayoutCountries(): Promise<PayoutCountry[]> {
    const response = await httpClientV2.get<V2PayoutCountriesResponse>(
      "payments/bank/countries"
    );
    return BankAccountMapperV2.toCountriesList(response);
  }

  async getBanksByCountry(countryCode: string): Promise<PayoutBank[]> {
    const response = await httpClientV2.get<V2PayoutBanksResponse>(
      `payments/bank/institutions?countryCode=${countryCode}`
    );
    return BankAccountMapperV2.toBanksList(response, countryCode);
  }

  async getBankBranches(bankId: number): Promise<BankBranch[]> {
    const response = await httpClientV2.get<V2BankBranchesResponse>(
      `payments/bank/branches/${bankId}`
    );
    return BankAccountMapperV2.toBranchesList(response);
  }
}
